import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Loader2,
  AlertCircle,
  Paperclip,
  FileText,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCreateLeave } from '../../services/httpServices/queries';
import { attachmentService } from '../../services/httpServices/attachmentService';
import { EmployeeSearchSelect } from '../../components/ui/EmployeeSearchSelect';
import { DatePicker } from '../../components/ui/DatePicker';

const LEAVE_TYPES = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SICK', label: 'Sick' },
  { value: 'CASUAL', label: 'Casual' },
  { value: 'MATERNITY', label: 'Maternity' },
  { value: 'PATERNITY', label: 'Paternity' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'COMPENSATORY', label: 'Compensatory' },
];

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
const labelClass = 'block text-sm font-medium text-text mb-1';
const sectionTitleClass =
  'text-sm font-semibold text-text uppercase tracking-wide border-b border-border pb-2 mb-4';

/** Matches the backend attachment upload limit (50 MB). */
const MAX_FILE_SIZE = 50 * 1024 * 1024;

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/** Inclusive calendar days between two ISO dates (0 when invalid). */
function inclusiveDays(start: string, end: string) {
  if (!start || !end) return 0;
  const s = new Date(`${start}T00:00:00`).getTime();
  const e = new Date(`${end}T00:00:00`).getTime();
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.round((e - s) / 86_400_000) + 1;
}

export function LeaveForm() {
  const navigate = useNavigate();
  const createLeave = useCreateLeave();

  const [employeeId, setEmployeeId] = useState('');
  const [type, setType] = useState('ANNUAL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ employee?: string; dates?: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const days = useMemo(() => inclusiveDays(startDate, endDate), [startDate, endDate]);
  const rangeInvalid = !!startDate && !!endDate && days === 0;

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;

    const accepted: File[] = [];
    for (const file of selected) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds the 50 MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
    // Reset so the same file can be re-selected after removal.
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: { employee?: string; dates?: string } = {};
    if (!employeeId) nextErrors.employee = 'Please select an employee';
    if (!startDate || !endDate) nextErrors.dates = 'Start and end dates are required';
    else if (days === 0) nextErrors.dates = 'End date cannot be before the start date';

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const response = await createLeave.mutateAsync({
        employeeId,
        type,
        startDate,
        endDate,
        days,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });

      // Attachments can only be linked once the leave record exists, so upload
      // the selected reference documents after the request has been created.
      const leaveId = response.data?.id;
      if (leaveId && files.length > 0) {
        const failed: string[] = [];
        for (const file of files) {
          try {
            await attachmentService.uploadFile(file, 'leave', leaveId, file.name);
          } catch {
            failed.push(file.name);
          }
        }
        if (failed.length > 0) {
          toast.error(
            `Leave request submitted, but ${failed.length} document(s) failed to upload: ${failed.join(', ')}`,
          );
        }
      }

      navigate('/hr?tab=leave');
    } catch {
      // Error toast is handled by the global MutationCache.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full space-y-4">
      <button
        onClick={() => navigate('/hr?tab=leave')}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
      >
        <ArrowLeft size={18} />
        Back to Leave
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
          <CalendarDays className="w-5 h-5 text-secondary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text">Apply for Leave</h1>
          <p className="text-sm text-text-muted">Submit a leave request for an employee</p>
        </div>
      </div>

      {(errors.employee || errors.dates || rangeInvalid) && (
        <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2 border border-danger/20">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">
            {errors.employee || errors.dates || 'End date cannot be before the start date'}
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-5 space-y-6">
        <div>
          <p className={sectionTitleClass}>Request Details</p>
          <div className="space-y-4">
            <EmployeeSearchSelect
              value={employeeId}
              onChange={(id) => {
                setEmployeeId(id);
                if (id) setErrors((prev) => ({ ...prev, employee: undefined }));
              }}
              label="Employee"
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Leave Type</label>
                <select value={type} onChange={(e) => setType(e.target.value)} className={inputClass}>
                  {LEAVE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Duration</label>
                <div className="w-full px-3 py-2 border border-border rounded-lg bg-background/50 text-sm text-text">
                  {days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '—'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Start Date <span className="text-danger">*</span>
                </label>
                <DatePicker
                  required
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    setErrors((prev) => ({ ...prev, dates: undefined }));
                  }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  End Date <span className="text-danger">*</span>
                </label>
                <DatePicker
                  required
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(value) => {
                    setEndDate(value);
                    setErrors((prev) => ({ ...prev, dates: undefined }));
                  }}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Optional reason for the leave request"
                className={`${inputClass} resize-none`}
              />
            </div>

            <div>
              <label className={labelClass}>Reference Documents</label>
              <p className="text-xs text-text-muted mb-2">
                Attach supporting documents such as a medical certificate or approval letter.
                You can select multiple files (max 50 MB each).
              </p>

              <input
                ref={fileInputRef}
                id="leave-attachments"
                type="file"
                multiple
                onChange={handleFilesSelected}
                className="hidden"
              />
              <label
                htmlFor="leave-attachments"
                className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-border rounded-lg bg-background text-sm text-text-muted hover:border-primary hover:text-text cursor-pointer transition-colors"
              >
                <Paperclip size={16} />
                Choose files
              </label>

              {files.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${file.lastModified}-${index}`}
                      className="flex items-center gap-3 p-2.5 bg-background rounded-lg border border-border"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText size={16} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text truncate">{file.name}</p>
                        <p className="text-xs text-text-muted">{formatFileSize(file.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-2 hover:bg-danger/10 rounded-lg transition-colors"
                        title="Remove"
                      >
                        <Trash2 size={16} className="text-danger" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate('/hr?tab=leave')}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Submit Request
          </button>
        </div>
      </form>
    </div>
  );
}
